import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Briefcase, MapPin, Clock, DollarSign } from 'lucide-react';
import api from '../../utils/api';
import { resolveAssetUrl } from '../../utils/url';
import { useTranslation } from 'react-i18next';

const CareerPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenantSlug } = useParams();
  const [company, setCompany] = useState<any>(null);
  const [positions, setPositions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [companyRes, positionsRes] = await Promise.all([
          api.get(`/api/tenants/public/${tenantSlug}`),
          api.get(`/api/positions/public/${tenantSlug}/positions`)
        ]);
        setCompany(companyRes.data);
        setPositions(positionsRes.data.positions);
      } catch (error) {
        console.error('Failed to fetch career page data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tenantSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lotus-gold mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('career.notFound')}</h1>
          <p className="text-gray-600">{t('career.notFoundDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Company Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-start gap-6">
            {company.logo_url && (
              <img src={resolveAssetUrl(company.logo_url)} alt={company.name} className="w-20 h-20 rounded-lg object-contain bg-white border" />
            )}
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{company.name}</h1>
              {company.description && (
                <p className="text-lg text-gray-600 mb-4">{company.description}</p>
              )}
              <div className="flex gap-4 text-sm text-gray-500">
                {company.industry && <span>• {company.industry}</span>}
                {company.company_size && <span>• {company.company_size} {t('career.employees')}</span>}
                {company.work_model && <span>• {company.work_model}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Positions List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('career.openPositions')}</h2>

        {positions.length === 0 ? (
          <div className="card text-center py-12">
            <Briefcase className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('career.noPositions')}</h3>
            <p className="text-gray-600">{t('career.checkBack')}</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {positions.map((position) => (
              <Link
                key={position.id}
                to={`/careers/${tenantSlug}/apply/${position.id}`}
                className="card hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{position.title}</h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{position.description}</p>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      {position.location && (
                        <div className="flex items-center gap-1">
                          <MapPin size={16} />
                          <span>{position.location}</span>
                        </div>
                      )}
                      {position.employment_type && (
                        <div className="flex items-center gap-1">
                          <Clock size={16} />
                          <span>{position.employment_type}</span>
                        </div>
                      )}
                      {position.salary_min && position.salary_max && (
                        <div className="flex items-center gap-1">
                          <DollarSign size={16} />
                          <span>
                            {position.salary_min} - {position.salary_max} {position.currency}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <button className="btn-primary ml-4">
                    {t('career.applyNow')}
                  </button>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CareerPage;
